import { useState, useMemo, useCallback, memo, useRef } from 'react'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { CategoriesBaseProps, CategoryItem, defaultCategories, categoryColors, formatChange } from './CategoriesBase'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const ProjectLogo = memo(({ logo, alt, color }: { logo?: string | null; alt: string; color?: string }) => {
  const [imgError, setImgError] = useState(false)
  
  return (
    <div 
      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: color || '#949494' }}
    >
      {!imgError && logo ? (
        <img
          src={logo}
          alt={alt}
          className="w-6 h-6 rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-[9px] font-medium text-white dark:text-[#fdfdfd]">
          {alt.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  )
})

ProjectLogo.displayName = 'ProjectLogo'

const CategoryItemComponent = memo(({ 
  item, 
  index, 
  onItemClick 
}: { 
  item: CategoryItem
  index: number
  onItemClick?: (item: CategoryItem) => void 
}) => {
  const change = item.change || 0
  const color = categoryColors[index % categoryColors.length]
  
  const handleClick = useCallback(() => {
    onItemClick?.(item)
  }, [item, onItemClick])
  
  const changeText = formatChange(change)
  
  return (
    <div
      className="bg-white dark:bg-[#1c1c1c] flex items-center justify-between pl-3 pr-[18px] py-2.5 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={handleClick}
    >
      <div className="flex gap-[10px] items-start">
        <ProjectLogo 
          logo={item.logo} 
          alt={item.project_name || 'Project'}
          color={color}
        />
        <div className="flex items-baseline">
          <p className="font-plus-jakarta font-medium leading-5 text-[#2d2923] dark:text-[#fdfdfd] text-sm overflow-hidden text-ellipsis whitespace-nowrap">
            {item.project_name || 'Project'}
          </p>
        </div>
      </div>
    </div>
  )
})

CategoryItemComponent.displayName = 'CategoryItemComponent'

export const CategoriesDesktop = ({ 
  items, 
  categories = defaultCategories,
  selectedCategory = '',
  onCategoryChange,
  onItemClick,
  loading = false
}: CategoriesBaseProps) => {
  const { scrollContainerRef, isDragging, handlers, style } = useHorizontalScroll()

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const filteredItems = useMemo(() => {
    return selectedCategory 
      ? items.filter(item => item.category === selectedCategory || item.industry === selectedCategory)
      : items
  }, [items, selectedCategory])

  const handleCategoryClick = useCallback((category: string) => {
    if (!isDragging) {
      onCategoryChange?.(category)
    }
  }, [isDragging, onCategoryChange])

  // Filter categories to only show those with orders
  const categoriesWithOrders = useMemo(() => {
    const allCategories = categories.length > 0 ? categories : defaultCategories
    return allCategories.filter(category => {
      return items.some(item => item.category === category || item.industry === category)
    })
  }, [categories, items])

  // Use first category as default if none selected or if selected category has no orders
  const displayCategories = categoriesWithOrders
  const activeCategory = (selectedCategory && displayCategories.includes(selectedCategory)) 
    ? selectedCategory 
    : displayCategories[0] || ''

  if (loading) {
    return (
      <div className="h-full flex flex-col relative">
        <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
          Categories
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading categories...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase mb-1.5">
        Categories
      </p>

      {/* Category tabs with arrows */}
      <div className="relative group mb-3">
        <button
          onClick={() => handleScroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-black/80 p-1 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronLeft size={16} />
        </button>
        
        <div 
          ref={scrollContainerRef}
          className="flex gap-5 items-center overflow-x-auto scrollbar-hide -mx-1 px-1"
          {...handlers}
          style={style}
        >
          {displayCategories.map((category) => {
            const isSelected = category === activeCategory
            return (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`font-plus-jakarta leading-4 text-xs pb-1.5 pt-0.5 whitespace-nowrap flex-shrink-0 transition-colors ${
                  isSelected
                    ? 'font-semibold text-[rgba(45,41,35,0.72)] dark:text-[#fdfdfd] border-b border-[rgba(45,41,35,0.72)] dark:border-[#fdfdfd]'
                    : 'font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] hover:text-[rgba(45,41,35,0.72)] dark:hover:text-[#fdfdfd]'
                }`}
              >
                {category}
              </button>
            )
          })}
        </div>

        <button
          onClick={() => handleScroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-black/80 p-1 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Project list */}
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {filteredItems.map((item, index) => (
          <CategoryItemComponent
            key={item.order_id || index}
            item={item}
            index={index}
            onItemClick={onItemClick}
          />
        ))}

        {filteredItems.length === 0 && (
          <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            No items in this category
          </div>
        )}
      </div>
    </div>
  )
}

